/**
 * Shared Field-Rule Helpers
 *
 * Common Zod field shapes re-declared across most `<entity>ValidationRules` objects
 * in this directory (issue #1407, part of #1402). Extracted so every entity schema
 * builds its field rules from one canonical shape per pattern instead of re-typing
 * `z.string().min(1).max(N)` (and its optional/union variants) per entity. Each helper
 * takes the exact message(s) the call site previously hard-coded, so extraction changes
 * zero validation behavior — same min/max/regex/message per field, just parameterized.
 */

import { z } from 'zod';
import { safeExternalHref } from '../../../lib/safeExternalHref';

/**
 * Required "name"-shaped field: non-empty, bounded length.
 * Matches the `<Entity> name is required` / `<Entity> name must be less than N characters`
 * pair repeated across every entity's `name` field (e.g. `nameField(200, 'Service name is
 * required', 'Service name must be less than 200 characters')`).
 */
export function nameField(
  maxLen: number,
  requiredMessage: string,
  maxMessage: string
): z.ZodString {
  return z.string().min(1, requiredMessage).max(maxLen, maxMessage);
}

/**
 * Required, non-empty string with no upper bound message beyond a simple presence check
 * (e.g. `businessOwnerId`, `processOwnerId`, `dataStewardId`, `leadUserId`). Matches the
 * repeated `z.string().min(1, '<Field> is required')` one-liner.
 */
export function requiredIdField(message: string): z.ZodString {
  return z.string().min(1, message);
}

/**
 * Optional bounded-length string with no special empty-string handling — an empty string
 * already satisfies `max()` on its own, so this is a plain `.optional()` (aka the
 * "descriptionField" shape in issue #1407, also used for notes/version/duration-style
 * fields). `message` is optional because several call sites never customized the default
 * Zod too-long message.
 */
export function optionalBoundedText(maxLen: number, message?: string): z.ZodOptional<z.ZodString> {
  return (message ? z.string().max(maxLen, message) : z.string().max(maxLen)).optional();
}

/**
 * Optional bounded-length string expressed as a union with `z.literal('')` — the shape
 * most `description`/`notes`/`category`-style optional fields use across team,
 * stakeholder, technology-capability, technology-domain, and
 * business-environment-factor schemas. Behaviorally equivalent to
 * {@link optionalBoundedText} for valid input; kept as its own helper so the split
 * preserves the exact pre-existing schema shape (and therefore identical Zod error
 * output) per field.
 */
export function optionalBoundedTextOrEmpty(
  maxLen: number,
  message: string
): z.ZodOptional<z.ZodUnion<readonly [z.ZodString, z.ZodLiteral<''>]>> {
  return z.union([z.string().max(maxLen, message), z.literal('')]).optional();
}

/**
 * True when `value` is an absolute `http:` / `https:` URL — the same test the
 * link sinks use ({@link safeExternalHref}), so a value the form accepts is one
 * the UI will render as a link.
 */
export function isHttpUrl(value: string): boolean {
  return safeExternalHref(value) !== null;
}

/**
 * Message for a URL field that is not http(s) (issue #2133). Edit forms
 * resubmit every field, so a value stored before the rule existed fails a save
 * that never touched it; the message names the field and says so (PR #2157).
 */
export function httpUrlRequirementMessage(fieldLabel: string): string {
  return `${fieldLabel} must start with http:// or https://. A value saved before this rule was introduced must be changed or cleared.`;
}

/**
 * Optional http(s)-only URL field that also accepts an empty string (used for
 * `repo`, `url`, `documentationUrl`, `websiteUrl`-style fields). `fieldLabel`
 * is the user-facing name used in both messages.
 */
export function optionalUrlField(
  fieldLabel: string,
  maxLen?: number
): z.ZodOptional<z.ZodUnion<readonly [z.ZodString, z.ZodLiteral<''>]>> {
  const url = z.string().url(`${fieldLabel} must be a valid URL`);
  const bounded =
    maxLen === undefined
      ? url
      : url.max(maxLen, `${fieldLabel} must be at most ${maxLen} characters`);
  return z
    .union([bounded.refine(isHttpUrl, httpUrlRequirementMessage(fieldLabel)), z.literal('')])
    .optional();
}

// Preprocess helpers for HTML form values.
// In Zod v4, z.preprocess (internally ZodPipeline) is not treated as optional by z.object
// even when the inner schema is optional. The outer .optional() is required so that
// missing keys are handled before the pipeline runs.

/** Converts an empty-string or null HTML form value to `undefined` before validating `schema`. */
export const emptyToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z
    .preprocess((val) => (val === '' || val === null ? undefined : val), z.optional(schema))
    .optional();

/** Converts a `NaN` numeric HTML form value to `undefined` before validating `schema`. */
export const nanToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z
    .preprocess(
      (val) => (typeof val === 'number' && isNaN(val) ? undefined : val),
      z.optional(schema)
    )
    .optional();
